// src/data/dependencies.ts
// Relations de dépendance entre items d'estimation.
// Première passe : relations intra-step (même catégorie).
// Les relations cross-step seront ajoutées dans une seconde passe.

export type SuggestionStrength = 'recommended' | 'optional';

export interface ItemSuggestion {
  targetKey: string;
  strength: SuggestionStrength;
  reason: string;
}

export interface ItemDependencies {
  suggests: ItemSuggestion[];
}

export const ITEM_DEPENDENCIES: Record<string, ItemDependencies> = {

  // ── DÉMOLITION ────────────────────────────────────────────────────────────

  'decaissement_profond': {
    suggests: [
      {
        targetKey: 'decaissement_radier',
        strength: 'recommended',
        reason: 'Un décaissement ≥ 40 cm nécessite généralement la création d\'un nouveau radier béton armé pour garantir la portance.',
      },
      {
        targetKey: 'decaissement_etancheite',
        strength: 'recommended',
        reason: 'La surface décaissée doit être cuvelée contre les remontées capillaires et l\'humidité.',
      },
      {
        targetKey: 'decaissement_enduit',
        strength: 'optional',
        reason: 'Les bas de murs mis à nu sur 50 cm nécessitent souvent une reprise d\'enduit après décaissement.',
      },
    ],
  },

  'decaissement_leger': {
    suggests: [
      {
        targetKey: 'decaissement_etancheite',
        strength: 'optional',
        reason: 'Même un décaissement léger peut exposer des zones sensibles à l\'humidité — un cuvelage est souvent recommandé.',
      },
    ],
  },

  'toitureamiantee_amiante': {
    suggests: [
      {
        targetKey: 'evacuation_dangereux',
        strength: 'recommended',
        reason: 'La dépose d\'une toiture amiantée génère des déchets dangereux qui doivent impérativement être évacués en filière agréée (BSDD).',
      },
    ],
  },

  'elementstoremove_cloisons': {
    suggests: [
      {
        targetKey: 'evacuation_inertes',
        strength: 'optional',
        reason: 'La démolition de cloisons génère des gravats inertes (plâtre, briques) à évacuer en déchetterie.',
      },
    ],
  },

  'elementstoremove_sols': {
    suggests: [
      {
        targetKey: 'evacuation_inertes',
        strength: 'optional',
        reason: 'La dépose de revêtements de sol (carrelage, chape) génère des gravats inertes à évacuer.',
      },
    ],
  },

  'demolitionlourde_mursporteurs': {
    suggests: [
      {
        targetKey: 'evacuation_inertes',
        strength: 'recommended',
        reason: 'La démolition d\'un mur porteur produit une grande quantité de gravats lourds à évacuer.',
      },
    ],
  },

  'demolitionlourde_tremies': {
    suggests: [
      {
        targetKey: 'evacuation_inertes',
        strength: 'optional',
        reason: 'La création de trémies dans le plancher génère des gravats béton ou bois à évacuer.',
      },
    ],
  },

  'demolitionlourde_cheminees': {
    suggests: [
      {
        targetKey: 'evacuation_inertes',
        strength: 'optional',
        reason: 'La démolition d\'une cheminée (briques, pierres, conduit) génère des gravats lourds à évacuer.',
      },
    ],
  },

  // ── GROS-ŒUVRE — Escaliers → Garde-corps ─────────────────────────────────
  // Tout escalier neuf doit être équipé d'un garde-corps conforme NF P01-012
  // (espacement max 11 cm, hauteur réglementaire 90 cm).

  'stairs_beton_coule': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012 (hauteur 90 cm, espacement max 11 cm).',
      },
    ],
  },

  'stairs_beton_prefabrique': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_metal_cremaillere': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_metal_limons': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_bois_droit': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_bois_quart_tournant': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_beton_permarche': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_metal_permarche': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_bois_permarche': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  // ── ISOLATION — ITE → Nettoyage façade (cross-step : isolation → exterieurs)

  'mursexterieurs_ite_polystyrene': {
    suggests: [
      {
        targetKey: 'facades_nettoyage',
        strength: 'recommended',
        reason: 'La pose d\'ITE exige un support propre et sain — un nettoyage haute pression de la façade est généralement requis au préalable.',
      },
    ],
  },

  'mursexterieurs_ite_polystyrene_r4': {
    suggests: [
      {
        targetKey: 'facades_nettoyage',
        strength: 'recommended',
        reason: 'La pose d\'ITE exige un support propre et sain — un nettoyage haute pression de la façade est requis au préalable.',
      },
    ],
  },

  'mursexterieurs_ite_laineroche': {
    suggests: [
      {
        targetKey: 'facades_nettoyage',
        strength: 'recommended',
        reason: 'La pose d\'ITE exige un support propre — un nettoyage haute pression de la façade est requis au préalable.',
      },
    ],
  },

  'mursexterieurs_ite_fibreboisbardage': {
    suggests: [
      {
        targetKey: 'facades_nettoyage',
        strength: 'recommended',
        reason: 'Avant la pose d\'un bardage bois en ITE, la façade doit être nettoyée et préparée.',
      },
    ],
  },

  // ── ISOLATION — ITI → Cloisons + Peinture (cross-step : isolation → cloisons + revetements)

  'mursexterieurs_iti_lainedeverre': {
    suggests: [
      {
        targetKey: 'cloisons_placoplatre',
        strength: 'recommended',
        reason: 'Une ITI en laine de verre se termine systématiquement par une doublage placo-plâtre pour protéger et finir l\'isolant.',
      },
      {
        targetKey: 'revetementsmuraux_peinture',
        strength: 'optional',
        reason: 'Le nouveau doublage placo issu de l\'ITI nécessite une finition peinture.',
      },
    ],
  },

  'mursexterieurs_iti_lainedeveerre_hydro': {
    suggests: [
      {
        targetKey: 'cloisons_placoplatre',
        strength: 'recommended',
        reason: 'Une ITI en laine de verre hydrophobe se termine par un doublage placo-plâtre.',
      },
      {
        targetKey: 'revetementsmuraux_peinture',
        strength: 'optional',
        reason: 'Le nouveau doublage placo issu de l\'ITI nécessite une finition peinture.',
      },
    ],
  },

  'mursexterieurs_iti_fibredebois': {
    suggests: [
      {
        targetKey: 'cloisons_placoplatre',
        strength: 'recommended',
        reason: 'Une ITI en fibre de bois se termine par un doublage placo-plâtre.',
      },
      {
        targetKey: 'revetementsmuraux_peinture',
        strength: 'optional',
        reason: 'Le nouveau doublage placo issu de l\'ITI nécessite une finition peinture.',
      },
    ],
  },

  // ── CLOISONS — Faux-plafonds → Peinture plafond (cross-step : cloisons → revetements)

  'fauxplafonds_placosuspendu': {
    suggests: [
      {
        targetKey: 'revetementsmuraux_peintureplafond',
        strength: 'recommended',
        reason: 'Tout faux-plafond placo neuf doit être peint — la peinture plafond est indissociable de la pose.',
      },
    ],
  },

  'fauxplafonds_placophonique': {
    suggests: [
      {
        targetKey: 'revetementsmuraux_peintureplafond',
        strength: 'recommended',
        reason: 'Tout faux-plafond placo neuf doit être peint — la peinture plafond est indissociable de la pose.',
      },
    ],
  },

  // ── CLOISONS — Aménagements spéciaux (cross-step)

  'amenagements_mezzanine': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Une mezzanine nécessite un garde-corps sécurisé conforme NF P01-012 et un escalier d\'accès.',
      },
    ],
  },

  'amenagements_poelebois': {
    suggests: [
      {
        targetKey: 'otherworks_renovationcheminees',
        strength: 'recommended',
        reason: 'L\'installation d\'un poêle à bois exige un conduit tubé inox double paroi conforme NF DTU 24.1.',
      },
    ],
  },

  // ── CROSS-STEP — Démolition → Gros-œuvre ─────────────────────────────────

  'demolitionlourde_mursporteurs': {
    suggests: [
      {
        targetKey: 'masonry_renforcementmurs_ipn',
        strength: 'recommended',
        reason: 'La suppression d\'un mur porteur nécessite la pose d\'une poutre IPN pour la reprise des charges en toiture et planchers.',
      },
      {
        targetKey: 'evacuation_inertes',
        strength: 'recommended',
        reason: 'La démolition d\'un mur porteur produit une grande quantité de gravats lourds à évacuer.',
      },
    ],
  },

  'demolitionlourde_tremies': {
    suggests: [
      {
        targetKey: 'stairs_beton_coule',
        strength: 'optional',
        reason: 'Une trémie dans le plancher est généralement créée pour accueillir un escalier — prévoir la création de l\'escalier.',
      },
      {
        targetKey: 'evacuation_inertes',
        strength: 'optional',
        reason: 'La création de trémies dans le plancher génère des gravats béton ou bois à évacuer.',
      },
    ],
  },

  // ── CROSS-STEP — Gros-œuvre → VRD ────────────────────────────────────────

  'otherworks_gouttieresacier': {
    suggests: [
      {
        targetKey: 'eauxpluviales_drains',
        strength: 'recommended',
        reason: 'Des gouttières neuves doivent être raccordées au réseau d\'évacuation des eaux pluviales pour éviter les infiltrations en pied de mur.',
      },
    ],
  },

  'otherworks_gouttierespvc': {
    suggests: [
      {
        targetKey: 'eauxpluviales_drains',
        strength: 'recommended',
        reason: 'Des gouttières neuves doivent être raccordées au réseau d\'évacuation des eaux pluviales.',
      },
    ],
  },

  'otherworks_gouttierescuivre': {
    suggests: [
      {
        targetKey: 'eauxpluviales_drains',
        strength: 'recommended',
        reason: 'Des gouttières neuves doivent être raccordées au réseau d\'évacuation des eaux pluviales.',
      },
    ],
  },

  // ── CROSS-STEP — Isolation ────────────────────────────────────────────────

  'combles_soufflage_lainedeverre': {
    suggests: [
      {
        targetKey: 'etancheitetoiture_toitterrasse_epdm',
        strength: 'optional',
        reason: 'L\'isolation des combles est souvent l\'occasion de vérifier et de traiter l\'étanchéité de la toiture.',
      },
    ],
  },

  // ── CROSS-STEP — Préparation → Finitions ─────────────────────────────────

  'diagnostics_termiteszonerisque': {
    suggests: [
      {
        targetKey: 'traitements_termites_curatif',
        strength: 'recommended',
        reason: 'Un diagnostic termites positif impose un traitement curatif obligatoire avant tous travaux sur les bois de structure.',
      },
    ],
  },

  // ── CROSS-STEP — Escaliers colimaçon → Garde-corps ──────────────────────

  'stairs_metal_colimacon': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  'stairs_bois_colimacon': {
    suggests: [
      {
        targetKey: 'stairs_gardecorps_interieur',
        strength: 'recommended',
        reason: 'Tout escalier neuf doit être équipé d\'un garde-corps conforme NF P01-012.',
      },
    ],
  },

  // ── CROSS-STEP — Permis → Démarches obligatoires ─────────────────────────

  'autorisations_permisavecarchitecte': {
    suggests: [
      {
        targetKey: 'autorisations_declarationouverturechantier',
        strength: 'recommended',
        reason: 'Tout permis de construire obtenu impose le dépôt d\'une déclaration d\'ouverture de chantier (DOC) avant démarrage des travaux.',
      },
      {
        targetKey: 'autorisations_daact',
        strength: 'recommended',
        reason: 'La DAACT est obligatoire à la fin de tout chantier réalisé sous permis de construire — sans elle, la revente du bien peut être bloquée.',
      },
    ],
  },

  'autorisations_permissansarchitecte': {
    suggests: [
      {
        targetKey: 'autorisations_declarationouverturechantier',
        strength: 'recommended',
        reason: 'Tout permis de construire obtenu impose le dépôt d\'une déclaration d\'ouverture de chantier (DOC) avant démarrage des travaux.',
      },
      {
        targetKey: 'autorisations_daact',
        strength: 'recommended',
        reason: 'La DAACT est obligatoire à la fin de tout chantier réalisé sous permis de construire — sans elle, la revente du bien peut être bloquée.',
      },
    ],
  },

  'autorisations_changementdestination': {
    suggests: [
      {
        targetKey: 'autorisations_permissansarchitecte',
        strength: 'optional',
        reason: 'Un changement de destination accompagné de travaux importants (surface > 20m²) peut nécessiter un permis de construire en complément.',
      },
    ],
  },

  // ── CROSS-STEP — Tranchée assainissement → Branchement réseau public ─────

  'raccordements_assainissement': {
    suggests: [
      {
        targetKey: 'raccordements_branchement_assainissement',
        strength: 'recommended',
        reason: 'La tranchée et la canalisation doivent être complétées par le branchement au réseau public d\'égout (jonction finale entre la canalisation privée et le réseau de la commune).',
      },
    ],
  },

  // ── CROSS-STEP — Assainissement individuel → SPANC ───────────────────────

  'raccordements_assainissementindividuel_fosseseptique': {
    suggests: [
      {
        targetKey: 'diagnostics_assainissement_spanc',
        strength: 'recommended',
        reason: 'L\'installation d\'une fosse septique nécessite un contrôle SPANC obligatoire pour valider la conformité de l\'assainissement non collectif.',
      },
    ],
  },

  'raccordements_assainissementindividuel_microstation': {
    suggests: [
      {
        targetKey: 'diagnostics_assainissement_spanc',
        strength: 'recommended',
        reason: 'L\'installation d\'une micro-station nécessite un contrôle SPANC obligatoire pour valider la conformité de l\'assainissement non collectif.',
      },
    ],
  },

  // ── CROSS-STEP — Sanitaire → Technique ───────────────────────────────────

  'sallesdebain_creation': {
    suggests: [
      {
        targetKey: 'plomberie_reseauxalimentation',
        strength: 'recommended',
        reason: 'La création d\'une salle de bain nécessite l\'installation de réseaux d\'alimentation en eau (eau chaude et eau froide).',
      },
      {
        targetKey: 'plomberie_reseauxevacuation',
        strength: 'recommended',
        reason: 'La création d\'une salle de bain nécessite l\'installation de réseaux d\'évacuation des eaux usées.',
      },
      {
        targetKey: 'electricite_tableauneuf',
        strength: 'recommended',
        reason: 'Une salle de bain neuve requiert un circuit électrique dédié conforme NF C 15-100 (prise rasoir, éclairage, sèche-serviettes).',
      },
    ],
  },

};

// ── Index inversé (cible → sources) ──────────────────────────────────────────
// Calculé une fois au niveau module pour les lookups O(1).

export const ITEM_DEPENDENCIES_BY_TARGET: Record<string, { sourceKey: string; strength: SuggestionStrength; reason: string }[]> = {};

Object.entries(ITEM_DEPENDENCIES).forEach(([sourceKey, dep]) => {
  dep.suggests.forEach(suggestion => {
    if (!ITEM_DEPENDENCIES_BY_TARGET[suggestion.targetKey]) {
      ITEM_DEPENDENCIES_BY_TARGET[suggestion.targetKey] = [];
    }
    ITEM_DEPENDENCIES_BY_TARGET[suggestion.targetKey].push({
      sourceKey,
      strength: suggestion.strength,
      reason: suggestion.reason,
    });
  });
});
